import { Injectable } from '@angular/core';
import { Order } from '@vendasta/sales-orders';
import { CustomFieldsAnswer, CustomFieldsAnswers } from '@vendasta/store';
import { Observable, Subject } from 'rxjs';
import { first, map, startWith, switchMap } from 'rxjs/operators';
import { AccountService } from '../../core/account.service';
import { ResellerItemOrderForm } from '../order-form-page/';
import { ActivationParamService } from './activation-param.service';

type CacheableFieldType = 'addon' | 'app' | 'order';

// The sole purpose of this service is to cache the order form submissions between the order form page and confirmation page
@Injectable({ providedIn: 'root' })
export class ProductOrderFormStoreService {
  readonly cachedAppOrderForms$: Observable<ResellerItemOrderForm[]>;
  readonly cachedAddonOrderForms$: Observable<ResellerItemOrderForm[]>;

  readonly cachedOrder$: Observable<Order>;

  private accountGroupId$: Observable<string>;
  private orderFormSaved$$: Subject<void> = new Subject<void>(); // used to refresh saved order form values.

  private dynamicOrderFormSaved$$: Subject<void> = new Subject<void>();

  constructor(
    private activationParamService: ActivationParamService,
    private accountService: AccountService,
  ) {
    this.accountGroupId$ = this.activationParamService.accountGroupId$;

    this.cachedAppOrderForms$ = this.savedOrderFormStream$('app');
    this.cachedAddonOrderForms$ = this.savedOrderFormStream$('addon');

    this.cachedOrder$ = this.savedOrderStream$('order');
  }

  static buildKeyForDynamicOrderForm(accountGroupId: string, appId: string): string {
    return `dynamic-${accountGroupId}-${appId}`;
  }

  cacheOrder(order: Order): void {
    this.saveOrder('order', order);
  }

  clearOrderCache(): void {
    this.accountGroupId$.pipe(first()).subscribe((accountGroupId) => {
      const key = this.buildKey(accountGroupId, 'order');
      window.sessionStorage.removeItem(key);
      this.orderFormSaved$$.next();
    });
  }

  // build a unique key for storage based on the account group id and the list of selected products.
  private buildKey(accountGroupId: string, type: CacheableFieldType): string {
    return `${accountGroupId}-${type}`;
  }

  // Return an order form stream calculated from the given type
  private savedOrderFormStream$(type: CacheableFieldType): Observable<any> {
    return this.orderFormSaved$$.pipe(
      startWith(undefined), // ensure we get the initial value
      switchMap(() => this.accountGroupId$),
      map((accountGroupId) => {
        const key = this.buildKey(accountGroupId, type);
        const orderFormJson = window.sessionStorage.getItem(key);
        if (orderFormJson) {
          return JSON.parse(orderFormJson);
        }
      }),
    );
  }

  private saveOrder(type: CacheableFieldType, order: Order): void {
    this.accountService.accountID$.pipe(first()).subscribe((accountGroupId) => {
      const key = this.buildKey(accountGroupId, type);
      const orderJson = JSON.stringify(order);
      window.sessionStorage.setItem(key, orderJson);
      this.orderFormSaved$$.next();
    });
  }

  // Return an order form stream calculated from the given type
  private savedOrderStream$(type: CacheableFieldType): Observable<Order> {
    return this.orderFormSaved$$.pipe(
      startWith(undefined), // ensure we get the initial value
      switchMap(() => this.accountGroupId$),
      map((accountGroupId) => {
        const key = this.buildKey(accountGroupId, type);
        const orderJson = window.sessionStorage.getItem(key);
        if (orderJson) {
          return JSON.parse(orderJson);
        }
      }),
    );
  }

  // Caches the value set in a custom/dynamic order form workflow to be prefilled by the order form component, overwriting the previous value
  cacheDynamicOrderForm(appId: string, customFieldsAnswers: CustomFieldsAnswer[]): void {
    this.accountGroupId$.pipe(first()).subscribe((accountGroupId) => {
      const key = ProductOrderFormStoreService.buildKeyForDynamicOrderForm(accountGroupId, appId);
      window.sessionStorage.setItem(key, JSON.stringify(customFieldsAnswers));
      this.dynamicOrderFormSaved$$.next();
    });
  }

  // Gets the order form custom field info for a given product
  getDynamicOrderForm$(appId: string): Observable<CustomFieldsAnswers[]> {
    return this.dynamicOrderFormSaved$$.pipe(
      startWith(undefined),
      switchMap(() => this.accountGroupId$),
      map((accountGroupId) => {
        const key = ProductOrderFormStoreService.buildKeyForDynamicOrderForm(accountGroupId, appId);
        const orderFormJson = window.sessionStorage.getItem(key);
        if (orderFormJson) {
          const fieldAnswer = JSON.parse(orderFormJson);
          return [
            {
              productID: appId,
              customFieldsAnswers: fieldAnswer,
            },
          ];
        }
      }),
    );
  }
}
